import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { animate, motion, useInView } from "framer-motion";
import {
  ArrowRight, Cloud, TrendingUp, Users, Settings, Blocks, BadgeCheck, BarChart3, Quote,
} from "lucide-react";
import { useLang } from "@/i18n";
import { Logo } from "@/components/Logo";
import { LangToggle } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const WORLDS = [
  { id: "aws", path: "/aws", accent: "#ff764a", icon: Cloud },
  { id: "martech", path: "/martech", accent: "#ca3b42", icon: TrendingUp },
  { id: "people", path: "/people-hr", accent: "#0d9488", icon: Users },
];

const BENEFIT_ICONS = [Settings, Blocks, BadgeCheck, BarChart3];
const BENEFIT_COLORS = ["#4361ee", "#ff764a", "#0d9488", "#ca3b42"];

const METRICS = [
  { to: 40, suffix: "h+", color: "#4361ee" },
  { text: "10–30%", color: "#ff764a" },
  { to: 12, prefix: "+", color: "#0d9488" },
  { to: 100, suffix: "%", color: "#ca3b42" },
];

const QUOTE_COLORS = ["#ff764a", "#ca3b42", "#4361ee"];

const CountUp = ({ to, prefix = "", suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    const c = animate(0, to, { duration: 1.6, ease: "easeOut", onUpdate: (v) => setVal(Math.round(v)) });
    return () => c.stop();
  }, [inView, to]);
  return <span ref={ref}>{prefix}{val}{suffix}</span>;
};

const HomeC = () => {
  const { t, lang } = useLang();
  const lit = t.homeLit;

  return (
    <div data-testid="home-c" className="min-h-[100svh] bg-[#FAFAFD] text-[#353535] flex flex-col">
      <div className="flex items-center justify-between px-5 sm:px-8 pt-6">
        <Logo />
        <LangToggle />
      </div>
      <div className="flex-1 flex flex-col justify-center px-5 sm:px-8 py-14">
        <div className="mx-auto w-full max-w-[1200px]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex flex-col items-center justify-center gap-5 sm:flex-row sm:gap-7"
          >
            <div data-testid="home-hero-eq" className="flex h-10 sm:h-12 items-end gap-[5px]" aria-hidden="true">
              {[14, 26, 42, 22, 34].map((h, i) => (
                <span
                  key={i}
                  className="eq-bar-slow w-[3px] rounded-full"
                  style={{ height: h, backgroundColor: BENEFIT_COLORS[i % 4], animationDelay: `${i * 0.22}s`, opacity: 0.45 + i * 0.13 }}
                />
              ))}
            </div>
            <h1 className="font-cabinet font-bold tracking-tight leading-[1.02] text-[10vw] sm:text-6xl lg:text-7xl text-center sm:text-left">
              {lang === "es" ? (
                <>Hazlo mejor <span className="text-[#4361ee]">con datos.</span></>
              ) : (
                <>Do it better <span className="text-[#4361ee]">with data.</span></>
              )}
            </h1>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 text-center font-jakarta text-base sm:text-lg text-[#999999] max-w-xl mx-auto"
          >
            {t.gateway.sub}
          </motion.p>

          <div className="mt-14 grid gap-4 md:grid-cols-3">
            {WORLDS.map((w, i) => {
              const c = t.gateway.worlds[w.id];
              const Icon = w.icon;
              return (
                <motion.div
                  key={w.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.3 + i * 0.1 }}
                >
                  <Link
                    to={w.path}
                    data-testid={`homec-select-${w.id}`}
                    className="group flex h-full flex-col rounded-2xl border border-black/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(42,42,42,0.10)]"
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl text-white" style={{ backgroundColor: w.accent }}>
                      <Icon size={22} />
                    </span>
                    <div className="mt-6 font-jbmono text-[10px] uppercase tracking-[0.22em]" style={{ color: w.accent }}>{c.tag}</div>
                    <h2 className="mt-2 font-cabinet text-2xl font-bold">{c.name}</h2>
                    <ul className="mt-4 flex flex-col gap-1.5 flex-1">
                      {c.points.map((p, j) => (
                        <li key={j} className="flex items-center gap-2 text-[13px] font-jakarta text-[#999999]">
                          <span className="h-1 w-1 rounded-full shrink-0" style={{ backgroundColor: w.accent }} /> {p}
                        </li>
                      ))}
                    </ul>
                    <span className="mt-6 inline-flex items-center gap-2 font-jbmono text-[11px] uppercase tracking-widest font-bold" style={{ color: w.accent }}>
                      {t.common.explore} <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* Literatura — impacto real */}
          <section data-testid="home-lit" className="mt-24 sm:mt-28 text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-4 sm:gap-5"
            >
              <h3 className="font-cabinet text-2xl sm:text-3xl font-bold tracking-tight text-[#353535]">
                {lit.impactTitle}
              </h3>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-5 max-w-3xl font-jakarta text-sm sm:text-base leading-relaxed text-[#888888]"
            >
              {lit.impact1}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.22 }}
              className="mt-4 max-w-3xl font-jakarta text-sm sm:text-base leading-relaxed font-semibold text-[#353535]"
            >
              {lit.impact3}
            </motion.p>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {lit.benefits.map((b, i) => {
                const Icon = BENEFIT_ICONS[i];
                return (
                  <motion.div
                    key={i}
                    data-testid={`home-lit-benefit-${i}`}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="relative flex min-h-[148px] flex-col justify-end overflow-hidden rounded-2xl p-6 text-white transition-transform duration-300 hover:-translate-y-1"
                    style={{ backgroundColor: BENEFIT_COLORS[i] }}
                  >
                    <Icon size={20} className="absolute right-5 top-5 text-white/70" />
                    <p className="font-jakarta text-[13px] font-semibold leading-snug">{b}</p>
                  </motion.div>
                );
              })}
            </div>

            {/* Banda de resultados */}
            <motion.div
              data-testid="home-lit-metrics"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7 }}
              className="mt-14 rounded-2xl border border-black/10 bg-white px-6 py-10 sm:px-10"
            >
              <div className="grid grid-cols-2 gap-y-10 lg:grid-cols-4">
                {METRICS.map((m, i) => (
                  <div key={i} data-testid={`home-lit-metric-${i}`} className="px-4 text-center lg:border-l lg:border-black/10 lg:first:border-l-0">
                    <div className="whitespace-nowrap font-cabinet text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight tabular-nums" style={{ color: m.color }}>
                      {m.to !== undefined ? <CountUp to={m.to} prefix={m.prefix || ""} suffix={m.suffix || ""} /> : m.text}
                    </div>
                    <p className="mx-auto mt-2 max-w-[210px] font-jakarta text-xs sm:text-sm leading-snug text-[#888888]">{lit.metrics[i]}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Testimonios (pendientes de contenido real) */}
            <div className="mt-16 sm:mt-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6 }}
              >
                <div className="font-jbmono text-[11px] uppercase tracking-[0.25em] text-[#4361ee]">{lit.testimonials.kicker}</div>
                <h3 className="mt-3 font-cabinet text-2xl sm:text-3xl font-bold tracking-tight text-[#353535]">{lit.testimonials.title}</h3>
              </motion.div>
              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {lit.testimonials.quotes.map((q, i) => (
                  <motion.figure
                    key={i}
                    data-testid={`home-lit-quote-${i}`}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="relative flex flex-col rounded-2xl border border-black/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(53,53,53,0.08)]"
                  >
                    <Quote size={22} style={{ color: QUOTE_COLORS[i] }} />
                    <blockquote className="mt-4 flex-1 font-jakarta text-sm leading-relaxed text-[#737373]">{q.text}</blockquote>
                    <figcaption className="mt-5 font-jbmono text-[10px] font-bold uppercase tracking-widest" style={{ color: QUOTE_COLORS[i] }}>{q.author}</figcaption>
                  </motion.figure>
                ))}
              </div>
            </div>
          </section>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HomeC;
